import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Users, Filter } from "lucide-react";
import api from "../lib/api";

export const STATUS_COLORS = {
  proposed: "#aeb8c6", fundraising: "#d6aa43", active: "#d6aa43", funded: "#4f9cf9",
  in_progress: "#f28c38", completed: "#3ec97a", monitoring: "#2dd4bf",
};
const STATUS_LABEL = { proposed: "Proposed", fundraising: "Fundraising", active: "Active", funded: "Funded", in_progress: "In progress", completed: "Completed", monitoring: "Monitoring" };
const LEGEND = Object.entries(STATUS_LABEL).filter(([k]) => k !== "active");
const MAP_CENTER = [9.5, 12.4];
const MAP_STYLE = { height: "100%", width: "100%", background: "#0a0d12" };
const COMMUNITY_PATH = { color: "#d6aa43", weight: 2, fillColor: "#d6aa43", fillOpacity: 0.12 };

export default function ImpactMap() {
  const [data, setData] = useState({ projects: [], communities: [], categories: [] });
  const [category, setCategory] = useState("all");
  const [showCommunities, setShowCommunities] = useState(true);

  useEffect(() => {
    document.title = "Impact Map | Dutsen Kauna Foundation";
    api.get("/impact-map").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const projects = useMemo(() => data.projects.filter((p) => category === "all" || p.category === category), [data, category]);
  const communities = useMemo(() => data.communities.filter((c) => category === "all" || (c.categories || {})[category]), [data, category]);
  const totalBen = communities.reduce((s, c) => s + c.beneficiaries + c.applications, 0);

  return (
    <div data-testid="impact-map-page" className="bg-[#155e3f] text-white">
      <section className="dk-container pt-14 pb-8">
        <span className="text-[#d6aa43] tracking-[0.3em] text-xs font-semibold uppercase">Impact map</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 max-w-3xl">Where the work happens.</h1>
        <p className="mt-4 text-white text-base md:text-lg max-w-2xl">Every project and beneficiary community across Nigeria, colour-coded by status. Filter by programme to see the footprint of each pillar.</p>
      </section>

      <section className="dk-container pb-16 grid lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1 space-y-5">
          <div className="rounded-2xl border border-white/10 bg-[#155e3f] p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#d6aa43] font-semibold mb-3"><Filter size={14} /> Programme</div>
            <div className="flex flex-wrap gap-2" data-testid="map-programme-filters">
              {["all", ...data.categories].map((c) => (
                <button key={c} onClick={() => setCategory(c)} data-testid={`map-filter-${c}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize border transition-colors ${category === c ? "bg-[#d6aa43] text-[#0a0d12] border-[#d6aa43]" : "border-white/15 text-white hover:border-[#d6aa43]"}`}>
                  {c.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#155e3f] p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#d6aa43] font-semibold mb-3">Project status</div>
            <ul className="space-y-2 text-sm" data-testid="map-legend">
              {LEGEND.map(([k, l]) => (
                <li key={k} className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: STATUS_COLORS[k] }} /> {l}</li>
              ))}
              <li className="flex items-center gap-2 pt-2 border-t border-white/10"><span className="w-3 h-3 rounded-full border-2 border-[#d6aa43]" /> Beneficiary community</li>
            </ul>
            <label className="mt-4 flex items-center gap-2 text-xs text-white">
              <input type="checkbox" checked={showCommunities} onChange={(e) => setShowCommunities(e.target.checked)} data-testid="map-toggle-communities" /> Show communities
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={MapPin} label="Projects" value={projects.length} testid="map-stat-projects" />
            <Stat icon={Users} label="People reached" value={totalBen} testid="map-stat-people" />
          </div>
        </aside>

        <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-white/10 h-[560px]" data-lenis-prevent data-testid="impact-map-container">
          <MapContainer center={MAP_CENTER} zoom={7} minZoom={5} maxZoom={12} scrollWheelZoom style={MAP_STYLE}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" className="dk-dark-tiles" />
            {showCommunities && communities.map((c) => (
              <CircleMarker key={c.state} center={[c.lat, c.lng]} radius={8 + Math.min(22, Math.sqrt(c.beneficiaries + c.applications) * 3)}
                pathOptions={COMMUNITY_PATH}>
                <Popup><div className="text-sm"><strong>{c.state} State</strong><br />{c.beneficiaries} registered beneficiaries<br />{c.applications} applications</div></Popup>
              </CircleMarker>
            ))}
            {projects.map((p) => (
              <CircleMarker key={p.serial || p.title} center={[p.lat, p.lng]} radius={9}
                pathOptions={{ color: "#0a0d12", weight: 1.5, fillColor: STATUS_COLORS[p.status] || "#d6aa43", fillOpacity: 0.95 }}>
                <Popup>
                  <div className="text-sm max-w-[220px]">
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: STATUS_COLORS[p.status] }}>{STATUS_LABEL[p.status] || p.status}</div>
                    <strong>{p.title}</strong>
                    <div className="text-xs text-black/60">{p.location || p.state} · {p.category}</div>
                    {p.beneficiaries_estimate > 0 && <div className="text-xs mt-1">{p.beneficiaries_estimate.toLocaleString()} beneficiaries</div>}
                    {p.serial && <Link to={`/projects/${p.serial}/trace`} className="text-xs font-bold underline text-[#0d1724] block mt-1">Trace {p.serial}</Link>}
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-[#155e3f] p-4" data-testid={testid}>
    <Icon size={16} className="text-[#d6aa43]" />
    <div className="font-serif text-3xl font-bold mt-2">{value.toLocaleString()}</div>
    <div className="text-xs text-white uppercase tracking-wider">{label}</div>
  </div>
);
